import * as routes from '@/pages/routes.ts';
import { withActionsMediaQuery } from '@/shared/lib/util/Util';
import { BarChartOutlined, DollarOutlined, LoginOutlined, UnlockOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Modal, Switch } from 'antd';
import { AdvertiserListItemBean, ListResponseAdvertiserListItemBean, LogBean } from 'common/api/api'
import { financialBlockApi } from 'common/api/FinancialBlockApi';
import { accountApi, advertiserApi } from 'common/api/entitiesApi';
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx';
import { ListActionButton } from 'common/components/ListActionButton.tsx';
import { TrustedStatusSelector } from 'common/components/dictionary/TrustedStatusSelector.tsx';
import { ListCard } from 'common/components/ListCard';
import { SearchInput } from 'common/components/SearchInput';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants';
import { encodeFilters } from 'common/util/urlPresets.ts';
import { initListFilters, useListFilters } from 'common/util/useListFilters';
import { apiCall, formatDollars, showError } from 'common/util/Util';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const AdvertiserList = () => {
    const appContext = useAppContext()
    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)
    const [loading, setLoading] = useState<boolean>(false)
    const [listData, setListData] = useState<ListResponseAdvertiserListItemBean>()
    const navigateTo = useNavigate()

    const loadData = () => {
        apiCall(
            setLoading,
            appContext,
            () =>
                advertiserApi.advPage(filters.sort?.name, filters.sort?.direction, filters.name, filters.trusted, filters.active, undefined, {
                    query: {page: filters.page, size: filters.pageSize}
                }),
            (data) => setListData(data)
        )
    }

    function loginAsAgency(id: number) {
        accountApi
            .loginAsAdvertiser(id)
            .then((response) => {
                window.open(response.data, '_blank')
            })
            .catch((e) => showError(appContext, e))
    }

    const onGoToReport = (record: AdvertiserListItemBean) => {
        if (!record.id) return

        const searchParams = new URLSearchParams({
            filters: encodeFilters({AdvertiserId: [record.id.toString()]}),
            period: 'LAST_7_DAYS',
            autogen: '1'
        }).toString()

        navigateTo(`/report-builder?${searchParams}`)
    }

    const toggleActive = (record: AdvertiserListItemBean, checked: boolean) => {
        if (record.id) {
            const id = record.id
            apiCall(
                setLoading,
                appContext,
                () => (checked ? advertiserApi.advertiserActivate(id) : advertiserApi.advertiserDeactivate(id)),
                () => {
                    record.active = checked
                    setListData(listData)
                }
            )
        }
    }

    const unblockPayments = (record: AdvertiserListItemBean) => {
        if (!record.id) return
        Modal.confirm({
            title: `Unblock payments for ${record.name}?`,
            content: 'Do this only after the refund or dispute has been reviewed.',
            okText: 'Unblock',
            onOk: () => financialBlockApi.unblockAdvertiser(record.id as number)
                .then(loadData)
                .catch((error) => showError(appContext, error)),
        })
    }

    return (
        <ListCard
            loading={loading}
            title="Advertisers"
            createName="Advertiser"
            createRoute={routes.ADVERTISER_CREATE}
            listRouteKey={routes.ADVERTISER}
            filters={filters}
            setFilters={setFilters}
            listData={listData}
            loadData={loadData}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    className: 'list-table-name',
                    sorter: true,
                    ellipsis: true,
                    render: (text, record) => (
                        <Link style={{color: '#5888CF', minWidth: 100}} to={`${routes.ADVERTISER}/${record.id}/edit`}>
                            {text}
                        </Link>
                    ),
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Accounts',
                    dataIndex: 'accounts',
                    render: (value, record) => (
                        <Link to={`/account?role=Advertiser&advertiserId=${record.id}`} style={{color: '#5888CF'}}>{value}</Link>
                    ),
                    width: 80
                },
                {
                    title: 'RevShare',
                    dataIndex: 'revShare',
                    render: (revShare: number) => `${revShare}%`,
                    width: 100
                },
                {
                    title: 'Time Zone',
                    dataIndex: 'timezone',
                    width: 80
                },
                {
                    title: 'Trusted',
                    dataIndex: 'trusted',
                    render: (val) => val ? 'yes' : 'no',
                    width: 70
                },
                {
                    title: 'Balance',
                    render: (bean: AdvertiserListItemBean) => formatDollars(bean.runtimeAvailable),
                    width: 100,
                },
                {
                    title: 'Total Paid',
                    render: (bean: AdvertiserListItemBean) => formatDollars(bean.total),
                    width: 100,
                },
                {
                    title: 'Last Modified',
                    dataIndex: 'modified',
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YY')
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    render: (val, record: AdvertiserListItemBean) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => record.id && toggleActive(record, checked)}
                        />
                    ),
                    width: 60
                },
                {
                    title: 'Actions',
                    key: 'action',
                    width: 120,
                    render: (_, record: AdvertiserListItemBean) => (
                        <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                            <ListActionButton onClick={() => loginAsAgency(record.id as number)} tooltip="Login As">
                                <LoginOutlined/>
                            </ListActionButton>
                            <ListActionButton onClick={() => navigateTo(`/logs/${LogBean.EntityEnum.Advertiser}/${record.id}`)}
                                    tooltip="Logs">
                                <UnorderedListOutlined/>
                            </ListActionButton>
                            <ListActionButton onClick={() => onGoToReport(record)} tooltip="Report">
                                <BarChartOutlined/>
                            </ListActionButton>
                            <ListActionButton onClick={() => navigateTo(`/transactions/create/${record.id}`)} tooltip="Add Transaction">
                                <DollarOutlined />
                            </ListActionButton>
                            {record.paymentBlocked && (
                                <ListActionButton danger onClick={() => unblockPayments(record)}
                                        tooltip={`Unblock payments${record.paymentBlockedReason ? `: ${record.paymentBlockedReason}` : ''}`}>
                                    <UnlockOutlined />
                                </ListActionButton>
                            )}
                        </div>
                    )
                }
            ]}
        >
            <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)}/>
            <TrustedStatusSelector
                placeholder="Filter by Trusted"
                style={{minWidth: '100px'}}
                value={filters.trusted}
                onChange={(value) => setFilter('trusted', value)}
            />
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{minWidth: '100px'}}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default withActionsMediaQuery('700px')(AdvertiserList)
